<template>
  <div class="pickup-point-detail">
    <div class="detail-header">
      <button @click="handleClose" class="close-button" type="button">
        <span>&times;</span>
      </button>
      <h3 class="detail-title">Detail výdejního místa</h3>
    </div>

    <div class="detail-content">
      <div class="pickup-point-info">
        <h4 class="pickup-point-name">{{ pickupPoint.name }}</h4>

        <div class="info-section">
          <div class="info-item address-item">
            <strong>Adresa:</strong>
            <div class="address-content">
              <p>{{ pickupPoint.street }}</p>
              <p>{{ pickupPoint.city }}, {{ pickupPoint.zip }}</p>
            </div>
          </div>

          <div v-if="pickupPoint.opening" class="info-item">
            <strong>Otevírací doba:</strong>
            <div class="opening-hours">
              <div
                v-for="day in dayOrder"
                :key="day"
                class="opening-day"
                v-show="pickupPoint.opening[day] !== undefined"
              >
                <span class="day-name">{{ getDayName(day) }}:</span>
                <span class="day-time">{{ pickupPoint.opening[day] || "Zavřeno" }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="detail-actions">
        <button @click="handleSelect" class="select-button" type="button">
          Vybrat výdejní místo
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { TPickupPoint } from "../types";

  interface Props {
    pickupPoint: TPickupPoint;
  }

  const props = defineProps<Props>();

  // Emits
  const emit = defineEmits<{
    close: [];
    select: [pickupPoint: TPickupPoint];
  }>();

  const handleClose = () => {
    emit("close");
  };

  const handleSelect = () => {
    emit("select", props.pickupPoint);
  };

  const dayOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

  const getDayName = (day: string): string => {
    const dayNames: Record<string, string> = {
      mon: "Pondělí",
      tue: "Úterý",
      wed: "Středa",
      thu: "Čtvrtek",
      fri: "Pátek",
      sat: "Sobota",
      sun: "Neděle",
    };
    return dayNames[day] || day;
  };
</script>

<style scoped lang="scss">
  .pickup-point-detail {
    background: white;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10;
    display: flex;
    flex-direction: column;

    .detail-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 15px;
      border-bottom: 1px solid #e0e0e0;
      position: relative;

      .detail-title {
        font-size: 1.4rem;
        font-weight: bold;
        margin: 0;
      }

      .close-button {
        position: absolute;
        right: 0;
        top: 0;
        width: 30px;
        height: 100%;
        border: none;
        background: transparent;

        span {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 2.5rem;
        }
      }
    }

    .detail-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 16px;

      .pickup-point-info {
        flex: 1;

        .pickup-point-name {
          margin: 0 0 16px 0;
          font-size: 1.9rem;
          font-weight: 600;
          color: #3399cc;
        }

        .info-section {
          .info-item {
            margin-bottom: 16px;

            strong {
              display: block;
              margin-bottom: 4px;
              color: #216384;
              font-size: 1.4rem;
            }

            p {
              margin: 0;
              color: #555;
              font-size: 1.4rem;
              line-height: 1.4;
            }

            &.address-item {
              .address-content {
                text-align: left;

                p {
                  text-align: left !important;
                  margin: 0;
                  color: #555;
                  font-size: 1.4rem;
                  line-height: 1.4;
                }
              }
            }

            .opening-hours {
              .opening-day {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 4px 0;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                  border-bottom: none;
                }

                .day-name {
                  font-weight: 500;
                  color: #333;
                  font-size: 14px;
                  min-width: 80px;
                }

                .day-time {
                  color: #555;
                  font-size: 14px;
                  text-align: right;
                }
              }
            }
          }
        }
      }

      .detail-actions {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #e0e0e0;

        .select-button {
          width: 100%;
          padding: 12px 24px;
          background: #3399cc;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background: #216384;
            transform: translateY(-1px);
          }

          &:active {
            transform: translateY(0);
          }
        }
      }
    }
  }

  @media (max-width: 579.98px) {
    .pickup-point-detail {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;

      .detail-content {
        padding: 10px 15px;

        .pickup-point-info {
          .pickup-point-name {
            font-size: 1.6rem;
            margin-bottom: 10px;
            color: #3399cc;
          }

          .info-section {
            .info-item {
              margin-bottom: 10px;
              strong {
                font-size: 1.3rem;
                color: #216384;
                margin-bottom: 2px;
              }

              .address-content {
                p {
                  font-size: 1.2rem !important;
                }
              }

              .opening-hours {
                .opening-day {
                  padding: 2px 0;

                  .day-name {
                    font-size: 1.3rem;
                  }

                  .day-time {
                    font-size: 1.3rem;
                  }
                }
              }
            }
          }
        }

        .detail-actions {
          padding-top: 0;
          margin-top: 10px;

          .select-button {
            padding: 10px 15px;
            font-size: 1.5rem;
          }
        }
      }
    }
  }
</style>
